
import React from 'react';
import { LoadingProgress } from './components/LoadingProgress';

export const ModernLoadingScreen: React.FC = () => {
  return (
    <div className="min-h-screen w-full relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-purple-50/30">
      {/* Interactive background elements */}
      <div className="absolute inset-0">
        {/* Animated gradient orbs */}
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-gradient-to-r from-purple-300/40 to-violet-300/40 rounded-full blur-3xl animate-pulse opacity-70" 
             style={{ animationDuration: '3s' }} />
        <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-gradient-to-r from-blue-300/35 to-indigo-300/35 rounded-full blur-3xl animate-pulse opacity-60" 
             style={{ animationDuration: '4s', animationDelay: '1s' }} />
        
        {/* Floating particles */}
        <div className="absolute top-1/3 left-1/3 w-2 h-2 bg-purple-400 rounded-full animate-ping opacity-60" 
             style={{ animationDelay: '0s' }} />
        <div className="absolute top-1/2 right-1/3 w-1.5 h-1.5 bg-violet-400 rounded-full animate-ping opacity-50" 
             style={{ animationDelay: '2s' }} />
        <div className="absolute bottom-1/3 left-1/2 w-1 h-1 bg-blue-400 rounded-full animate-ping opacity-40" 
             style={{ animationDelay: '4s' }} />
      </div>
      
      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6">
        {/* Enhanced logo section */}
        <div className="mb-12 relative group">
          {/* Interactive glow effect */}
          <div className="absolute -inset-12 bg-gradient-to-r from-purple-400/30 to-violet-400/30 rounded-full blur-2xl opacity-60 animate-pulse group-hover:opacity-80 transition-opacity duration-500" 
               style={{ animationDuration: '2s' }} />
          
          {/* Rotating ring around logo */}
          <div className="absolute -inset-8 border-2 border-purple-200/30 rounded-full animate-spin opacity-40" 
               style={{ animationDuration: '8s' }} />
          
          {/* Logo with hover effects */}
          <img 
            src="https://framerusercontent.com/images/9N8Z1vTRbJsHlrIuTjm6Ajga4dI.png" 
            alt="Pullse AI Logo" 
            className="w-20 h-20 object-contain relative z-10 drop-shadow-lg 
                       transition-all duration-300 ease-out hover:scale-110 cursor-pointer
                       filter hover:brightness-110"
          />
        </div>
        
        {/* Interactive loading spinner */}
        <div className="mb-10 relative">
          <div className="relative w-20 h-20">
            {/* Multiple animated rings */}
            <div className="absolute inset-0 border-4 border-transparent border-t-purple-500 border-r-purple-400 rounded-full animate-spin" />
            <div className="absolute inset-2 border-3 border-transparent border-b-violet-400 border-l-violet-300 rounded-full animate-spin" 
                 style={{ animationDirection: 'reverse', animationDuration: '1.2s' }} />
            <div className="absolute inset-4 border-2 border-transparent border-t-indigo-400 rounded-full animate-spin" 
                 style={{ animationDuration: '2s' }} />
            
            {/* Pulsing center dot */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-3 h-3 bg-gradient-to-r from-purple-500 to-violet-500 rounded-full animate-pulse shadow-lg" 
                   style={{ animationDuration: '1.5s' }} />
            </div>
          </div>
        </div>
        
        {/* Enhanced loading text */}
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent mb-3">
            Loading
          </h2>
          <p className="text-slate-600 text-lg animate-pulse">
            Preparing your workspace
          </p>
        </div>
        
        {/* Interactive progress section */}
        <LoadingProgress />
        
        {/* Enhanced status indicator with pulse effect */}
        <div className="mt-10 flex items-center gap-4 px-8 py-4 bg-white/70 backdrop-blur-md rounded-full border border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer">
          <div className="relative">
            <div className="w-3 h-3 bg-purple-500 rounded-full animate-pulse" />
            <div className="absolute inset-0 w-3 h-3 bg-purple-400 rounded-full animate-ping opacity-75" />
          </div>
          <span className="text-slate-700 text-sm font-semibold">
            Initializing components...
          </span>
        </div>
      </div>
    </div>
  );
};
